import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import palette from 'src/theme/palette';

import { formatPercent, formatShortDays } from './formatters';
import { ProductThumb } from './ProductThumb';
import type { RetentionSecondPurchasePairStat } from './types';

interface SecondPurchasesTableProps {
  rows: RetentionSecondPurchasePairStat[];
  /** Default 6 (card); use 20 in modal. */
  maxRows?: number;
  showCaption?: boolean;
}

const headCellSx = {
  color: palette.text.secondary,
  fontWeight: 700,
  fontSize: '0.8125rem',
  letterSpacing: '0.02em',
  borderBottom: `1px solid ${palette.grey[200]}`,
  py: 1.25,
  whiteSpace: 'nowrap',
} as const;

const bodyCellSx = {
  borderBottom: `1px solid ${palette.grey[200]}`,
  py: 1.35,
  verticalAlign: 'middle',
} as const;

/** Titoli lunghi: preferenza su spazi tra parole, evita colonne troppo strette. */
const productTitleSx = {
  minWidth: 0,
  flex: 1,
  fontSize: '0.88rem',
  lineHeight: 1.35,
  overflowWrap: 'break-word' as const,
  wordBreak: 'normal' as const,
  hyphens: 'auto' as const,
};

export function SecondPurchasesTable({
  rows,
  maxRows = 6,
  showCaption = true,
}: SecondPurchasesTableProps) {
  const displayRows = rows.slice(0, maxRows);

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <TableContainer
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          borderRadius: 1,
        }}
      >
        <Table
          size="small"
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            minWidth: 720,
          }}
        >
          <colgroup>
            <col style={{ width: '34%' }} />
            <col style={{ width: '34%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, pl: 0.5 }}>
                First product
              </TableCell>
              <TableCell sx={headCellSx}>Most common 2nd purchase</TableCell>
              <TableCell align="right" sx={{ ...headCellSx, pr: 1 }}>
                Avg delay
              </TableCell>
              <TableCell align="right" sx={{ ...headCellSx, pr: 1 }}>
                Repeat rate
              </TableCell>
              <TableCell align="right" sx={{ ...headCellSx, pr: 0.5 }}>
                Customers
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows.map((row) => (
              <TableRow
                key={`${row.firstProductId}-${row.secondProductId}`}
                hover
                sx={{
                  '&:last-of-type .MuiTableCell-root': {
                    borderBottom: 'none',
                  },
                }}
              >
                <TableCell sx={{ ...bodyCellSx, pl: 0.5 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1.25}
                    sx={{ minWidth: 0 }}
                  >
                    <Box sx={{ flexShrink: 0, lineHeight: 0 }}>
                      <ProductThumb
                        imageUrl={row.firstProductImageUrl}
                        title={row.firstProductTitle}
                        size={38}
                        radius={10}
                      />
                    </Box>
                    <Typography
                      sx={{
                        ...productTitleSx,
                        fontWeight: 700,
                        color: palette.text.primary,
                      }}
                    >
                      {row.firstProductTitle}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1.25}
                    sx={{ minWidth: 0 }}
                  >
                    <Box sx={{ flexShrink: 0, lineHeight: 0 }}>
                      <ProductThumb
                        imageUrl={row.secondProductImageUrl}
                        title={row.secondProductTitle}
                        size={34}
                        radius={10}
                      />
                    </Box>
                    <Typography
                      sx={{
                        ...productTitleSx,
                        fontWeight: 600,
                        color: palette.text.secondary,
                      }}
                    >
                      {row.secondProductTitle}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ ...bodyCellSx, pr: 1 }}>
                  <Typography
                    sx={{
                      color: palette.text.secondary,
                      fontWeight: 600,
                      fontSize: '0.88rem',
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatShortDays(row.avgDelayDays)}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ ...bodyCellSx, pr: 1 }}>
                  <Typography
                    sx={{
                      color: palette.text.primary,
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatPercent(row.repeatRate ?? undefined)}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ ...bodyCellSx, pr: 0.5 }}>
                  <Typography
                    sx={{
                      color: palette.text.primary,
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.customers.toLocaleString()}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {showCaption ? (
        <Typography
          variant="caption"
          sx={{
            color: palette.text.secondary,
            display: 'block',
            mt: 1.25,
            lineHeight: 1.45,
            fontSize: '0.75rem',
          }}
        >
          Sorted by how often each first-to-second product pair appears. Repeat
          rate is customers in that pair divided by first orders that included
          the first product.
        </Typography>
      ) : null}
    </Box>
  );
}

export default SecondPurchasesTable;
